import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CalendarDays, Check, ClipboardList, Crown, Hourglass, Link2, Loader2, MapPin, Plus, Save, Shield, Target, Trash2, Users, X } from 'lucide-react';
import { api } from '../../services/api';
import { SessionAnswersReadOnly, SessionAnswersData } from '../session-hub/SessionAnswers';

type Mode = 'in_person' | 'remote_teams';
type Retention = 3 | 6 | 12;

interface Attendee {
  email: string;
  name?: string;
  organisation?: string;
  role?: string;
  mode: Mode;
  outsideClientDomains?: boolean;
  /** Set in the form when the user confirms an address outside the domains */
  allowOutsideDomains?: boolean;
}

interface AgendaItem { time: string; title: string; note?: string }

interface DayLink {
  sessionId: string;
  sessionName: string;
  retention?: { outcome: 'scheduled' | 'kept_existing' | 'none'; deleteOn?: string };
}

interface Pack {
  bookingId: string;
  linked: boolean;
  days: string[];
  clientDomains: string[];
  retentionMonths: Retention | null;
  accessNotes: string;
  agendaByDate: Record<string, AgendaItem[]>;
  attendees: Attendee[];
  sponsorEmail: string | null;
  links: Record<string, DayLink>;
  updatedAt: string | null;
}

interface Props {
  bookingId: string;
  title: string;
  onClose: () => void;
}

const input = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
const small = 'border border-[#e2e8f0] rounded-lg h-9 px-2 text-sm text-navy bg-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
const sectionLabel = 'text-[11px] font-bold text-primary tracking-widest uppercase flex items-center gap-1.5';
const secondaryBtn = 'flex items-center justify-center gap-1.5 rounded-lg h-9 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-60';

const fmtDay = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const errorOf = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';

const RETENTION_TEXT: Record<NonNullable<DayLink['retention']>['outcome'], (d?: string) => string> = {
  scheduled: (d) => `Content deletion scheduled for ${d ? fmtDay(d) : 'the agreed date'}.`,
  kept_existing: (d) => `The session already had a deletion date (${d ? fmtDay(d) : 'set'}); it was left as it is.`,
  none: (d) => (d ? `The agreed date (${fmtDay(d)}) has passed, so nothing was scheduled. Set one on the session.` : 'No deletion date yet: choose the agreed retention above.'),
};

/**
 * The Session Hub pack for one studio booking: who is coming, the agenda,
 * how to get there and the agreed retention. It opens once a facilitator has
 * linked a session to the booking (from the project's Sessions page). Account
 * managers fill in everything but the agenda, which the facilitator sets.
 */
export function BookingPackDrawer({ bookingId, title, onClose }: Props) {
  const base = `/api/studio/bookings/${bookingId}/pack`;
  const [pack, setPack] = useState<Pack | null>(null);
  const [canEditAgenda, setCanEditAgenda] = useState(false);
  const [answers, setAnswers] = useState<Record<string, SessionAnswersData>>({});
  const [form, setForm] = useState<Pack | null>(null);
  const [domainsText, setDomainsText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [day, setDay] = useState<string>('');
  const [newAttendee, setNewAttendee] = useState<Attendee>({ email: '', mode: 'in_person' });

  const adopt = (p: Pack) => {
    setPack(p);
    setForm(JSON.parse(JSON.stringify(p)));
    setDomainsText(p.clientDomains.join(', '));
    setDay((d) => d || p.days[0] || '');
  };

  useEffect(() => {
    api.get<{ pack: Pack; canEditAgenda: boolean; answers?: Record<string, SessionAnswersData> }>(base)
      .then((r) => { adopt(r.data.pack); setCanEditAgenda(r.data.canEditAgenda); setAnswers(r.data.answers ?? {}); })
      .catch((e) => setError(errorOf(e)));
  }, [base]);

  const domains = useMemo(() => domainsText.split(/[\s,]+/).map((d) => d.trim().toLowerCase().replace(/^@/, '')).filter(Boolean), [domainsText]);
  const outside = (email: string) => !domains.includes(email.split('@')[1]?.toLowerCase() ?? '');

  if (!form || !pack) {
    return (
      <Shell title={title} onClose={onClose}>
        {error ? <ErrorBanner text={error} /> : <div className="flex justify-center py-20 text-[#64748b]"><Loader2 size={22} className="animate-spin" /></div>}
      </Shell>
    );
  }

  if (!pack.linked) {
    return (
      <Shell title={title} onClose={onClose}>
        <div className="p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4"><Hourglass size={24} className="text-slate-400" /></div>
          <p className="text-sm font-semibold text-slate-600">Waiting for the facilitator to set up the Session Hub</p>
          <p className="text-xs text-slate-400 mt-2">The facilitator links their session to this booking from the project's Sessions page. You'll get an email when the pack is ready to fill in.</p>
        </div>
      </Shell>
    );
  }

  const set = <K extends keyof Pack>(key: K, value: Pack[K]) => setForm({ ...form, [key]: value });
  const setAttendee = (i: number, patch: Partial<Attendee>) => set('attendees', form.attendees.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const agenda = form.agendaByDate[day] ?? [];
  const setAgenda = (items: AgendaItem[]) => set('agendaByDate', { ...form.agendaByDate, [day]: items });

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorOf(e));
    } finally {
      setBusy(null);
    }
  }

  const save = () => run('save', async () => {
    const r = await api.put<{ pack: Pack }>(base, {
      clientDomains: domains,
      retentionMonths: form.retentionMonths,
      accessNotes: form.accessNotes,
      ...(canEditAgenda ? { agendaByDate: Object.fromEntries(Object.entries(form.agendaByDate).map(([d, items]) => [d, items.filter((i) => i.title.trim())])) } : {}),
      attendees: form.attendees.map(({ outsideClientDomains, ...a }) => a),
      sponsorEmail: form.sponsorEmail,
    });
    adopt(r.data.pack);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  });

  const addAttendee = () => {
    const email = newAttendee.email.trim().toLowerCase();
    if (!email || form.attendees.some((a) => a.email === email)) return;
    set('attendees', [...form.attendees, { ...newAttendee, email }]);
    setNewAttendee({ email: '', mode: 'in_person' });
  };

  return (
    <Shell title={title} onClose={onClose}>
      {error && <ErrorBanner text={error} />}

      <section className="p-5 border-b border-[#e2e8f0] flex flex-col gap-3">
        <h3 className={sectionLabel}><Shield size={14} /> Client and retention</h3>
        <div>
          <label htmlFor="pack-domains" className="block text-sm font-semibold text-navy mb-1">Client email domains</label>
          <input id="pack-domains" className={input} value={domainsText} onChange={(e) => setDomainsText(e.target.value)} placeholder="acme.com, acme.ie" />
          <p className="mt-1 text-xs text-slate-400">Session Hub email only goes to these domains, unless you confirm a single address.</p>
        </div>
        <div>
          <p className="block text-sm font-semibold text-navy mb-1">Retention agreed with the client</p>
          <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden max-w-sm">
            {([null, 3, 6, 12] as Array<Retention | null>).map((m) => (
              <button
                key={String(m)} type="button" onClick={() => set('retentionMonths', m)}
                className={`flex-1 py-2 text-xs font-semibold transition-all ${form.retentionMonths === m ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
              >
                {m === null ? 'Not agreed' : `${m} months`}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-400">Session content is deleted this long after each session day, once the day is linked to its session.</p>
        </div>
      </section>

      <section className="p-5 border-b border-[#e2e8f0] flex flex-col gap-3">
        <h3 className={sectionLabel}><Users size={14} /> Attendees</h3>
        {form.attendees.length === 0 && <p className="text-xs text-slate-400">No attendees yet.</p>}
        <ul className="flex flex-col gap-2">
          {form.attendees.map((a, i) => {
            const out = outside(a.email);
            return (
              <li key={a.email} className="border border-[#e2e8f0] rounded-xl p-3 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-navy flex-1 min-w-[12rem]">{a.email}</span>
                  <button
                    type="button" title={form.sponsorEmail === a.email ? 'Sponsor' : 'Make sponsor'}
                    onClick={() => set('sponsorEmail', form.sponsorEmail === a.email ? null : a.email)}
                    className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${form.sponsorEmail === a.email ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-white text-slate-400 border-slate-200 hover:text-amber-700'}`}
                  >
                    <Crown size={12} /> {form.sponsorEmail === a.email ? 'Sponsor' : 'Make sponsor'}
                  </button>
                  <button type="button" title="Remove" onClick={() => set('attendees', form.attendees.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <input aria-label={`Name of ${a.email}`} className={small} placeholder="Name" value={a.name ?? ''} onChange={(e) => setAttendee(i, { name: e.target.value })} />
                  <input aria-label={`Organisation of ${a.email}`} className={small} placeholder="Organisation" value={a.organisation ?? ''} onChange={(e) => setAttendee(i, { organisation: e.target.value })} />
                  <input aria-label={`Role of ${a.email}`} className={small} placeholder="Role" value={a.role ?? ''} onChange={(e) => setAttendee(i, { role: e.target.value })} />
                  <select aria-label={`Attendance of ${a.email}`} className={small} value={a.mode} onChange={(e) => setAttendee(i, { mode: e.target.value as Mode })}>
                    <option value="in_person">In person</option>
                    <option value="remote_teams">Teams</option>
                  </select>
                </div>
                {out && !a.outsideClientDomains && (
                  <label className="flex items-center gap-2 text-xs text-amber-700">
                    <input type="checkbox" checked={!!a.allowOutsideDomains} onChange={(e) => setAttendee(i, { allowOutsideDomains: e.target.checked })} />
                    Outside the client domains. Confirm this one address is right.
                  </label>
                )}
                {a.outsideClientDomains && <p className="text-xs text-slate-400">Outside the client domains (confirmed).</p>}
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap items-center gap-2">
          <input
            aria-label="New attendee email" type="email" className={`${small} flex-1 min-w-[14rem]`} placeholder="name@client.com"
            value={newAttendee.email} onChange={(e) => setNewAttendee({ ...newAttendee, email: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAttendee(); } }}
          />
          <select aria-label="New attendee attendance" className={small} value={newAttendee.mode} onChange={(e) => setNewAttendee({ ...newAttendee, mode: e.target.value as Mode })}>
            <option value="in_person">In person</option>
            <option value="remote_teams">Teams</option>
          </select>
          <button type="button" className={secondaryBtn} onClick={addAttendee}><Plus size={14} /> Add</button>
        </div>
      </section>

      <section className="p-5 border-b border-[#e2e8f0] flex flex-col gap-3">
        <h3 className={sectionLabel}><ClipboardList size={14} /> Agenda</h3>
        {form.days.length > 1 && (
          <div className="flex flex-wrap gap-1">
            {form.days.map((d) => (
              <button key={d} type="button" onClick={() => setDay(d)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${d === day ? 'bg-primary/10 text-primary' : 'text-slate-500 hover:bg-slate-50'}`}>{fmtDay(d)}</button>
            ))}
          </div>
        )}
        {canEditAgenda ? (
          <>
        {agenda.map((item, i) => (
            <div key={i} className="grid grid-cols-[6rem_1fr_1fr_auto] gap-2 items-center">
              <input aria-label="Time" type="time" className={small} value={item.time} onChange={(e) => setAgenda(agenda.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))} />
              <input aria-label="Agenda item" className={small} placeholder="Item" value={item.title} onChange={(e) => setAgenda(agenda.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
              <input aria-label="Note" className={small} placeholder="Note (optional)" value={item.note ?? ''} onChange={(e) => setAgenda(agenda.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} />
              <button type="button" title="Remove item" onClick={() => setAgenda(agenda.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" className={`${secondaryBtn} self-start`} onClick={() => setAgenda([...agenda, { time: agenda.at(-1)?.time ?? '09:30', title: '' }])}><Plus size={14} /> Add agenda item</button>
          </>
        ) : agenda.length === 0 ? (
          <p className="text-xs text-slate-400">The facilitator hasn't added the agenda for this day yet.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {agenda.map((item, i) => (
              <li key={i} className="text-sm text-slate-600"><span className="font-semibold text-navy mr-2">{item.time}</span>{item.title}{item.note ? <span className="text-slate-400"> · {item.note}</span> : null}</li>
            ))}
          </ul>
        )}
      </section>

      <section className="p-5 border-b border-[#e2e8f0] flex flex-col gap-2">
        <h3 className={sectionLabel}><MapPin size={14} /> Access notes</h3>
        <textarea aria-label="Access notes" rows={3} className={`${input} resize-y`} maxLength={1000} value={form.accessNotes} onChange={(e) => set('accessNotes', e.target.value)} placeholder="Anything this client needs beyond the room's own arrival instructions" />
      </section>

      <div className="px-5 py-4 border-b border-[#e2e8f0] bg-slate-50/50 flex items-center justify-end gap-3">
        {saved && <span className="text-xs text-green-600 flex items-center gap-1"><Check size={14} /> Saved</span>}
        <button type="button" onClick={save} disabled={!!busy} className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60">
          {busy === 'save' ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save pack
        </button>
      </div>

      <section className="p-5 flex flex-col gap-3">
        <h3 className={sectionLabel}><Link2 size={14} /> Linked sessions</h3>
        {pack.days.map((d) => {
          const l = pack.links[d];
          return (
            <div key={d} className="border border-[#e2e8f0] rounded-xl p-3 flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-navy w-28 flex items-center gap-1.5"><CalendarDays size={14} className="text-slate-400" /> {fmtDay(d)}</span>
                <span className="text-sm text-slate-600 flex-1">{l ? l.sessionName : <span className="text-slate-400">No session linked for this day</span>}</span>
              </div>
              {l?.retention && <p className="text-xs text-slate-500">{RETENTION_TEXT[l.retention.outcome](l.retention.deleteOn)}</p>}
            </div>
          );
        })}
      </section>

      {Object.keys(answers).length > 0 && (
        <section className="p-5 border-t border-[#e2e8f0] flex flex-col gap-4">
          <h3 className={sectionLabel}><Target size={14} /> Objective and what attendees want</h3>
          <p className="text-xs text-slate-400">From the client's Session Hub. The facilitator sets the objective and the sponsor confirms it. Attendees never see each other's answers.</p>
          {Object.entries(answers).map(([sessionId, a]) => {
            const name = Object.values(pack.links).find((l) => l.sessionId === sessionId)?.sessionName ?? 'Session';
            return (
              <div key={sessionId} className="border border-[#e2e8f0] rounded-xl p-3 flex flex-col gap-2">
                {Object.keys(answers).length > 1 && <p className="text-sm font-semibold text-navy">{name}</p>}
                <SessionAnswersReadOnly answers={a} />
              </div>
            );
          })}
        </section>
      )}
    </Shell>
  );
}

function Shell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" role="dialog" aria-modal="true" aria-label={`Session Hub pack: ${title}`} onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-white shadow-xl flex flex-col font-display" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Session Hub pack</p>
            <h2 className="text-lg font-bold text-navy truncate">{title}</h2>
          </div>
          <button type="button" onClick={onClose} title="Close" className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ErrorBanner({ text }: { text: string }) {
  return (
    <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
      <p className="text-sm font-medium text-red-700">{text}</p>
    </div>
  );
}
